import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ContactUsSectionWrapper,
  HeroWrapper,
  ContentWrapper,
  Heading,
  FormContainer,
  FormHeading,
  FormInner,
  Input,
  Button,
  ContactsSection,
  ContactItem,
  ContactIconWrapper,
  ContactLink,
  BackgroundImage,
  BackgroundBlur,
} from "./styles";
import {
  CONTACT_ADDRESS_SHORT_STR,
  CONTACT_EMAIL,
  CONTACT_PHONE_NUMBER_DISPLAY,
  CONTACT_PHONE_NUMBER_RAW,
  HOMEPAGE_ANCHOR_CONTAINER_IDs,
} from "../../../lib/constants";
import EnvelopeIconUrl from "./svg/envelope-icon.svg";
import MapPinIconUrl from "./svg/map-pin-icon.svg";
import PhoneIconUrl from "./svg/phone-icon.svg";
import { requestDemo, validateEmail } from "../../../lib/contactHelper";
import { DecorativeDot } from "../../../lib/styles";

const formatPhoneNumber = (value) => {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, "");
  
  // Limit to 10 digits for US phone numbers
  const limitedDigits = digitsOnly.slice(0, 10);
  
  // Format as (XXX) XXX-XXXX
  if (limitedDigits.length === 0) return "";
  if (limitedDigits.length <= 3) return `(${limitedDigits}`;
  if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  }
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
};

export function ContactUsSection() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const hasErrors = !!emailError || !!phoneError;

  const validateEmailField = (value) => {
    if (!value) {
      setEmailError("");
      return;
    }
    if (!validateEmail(value)) {
      setEmailError("Invalid email address");
    } else {
      setEmailError("");
    }
  };

  const validatePhoneField = (value) => {
    if (!value) {
      setPhoneError("");
      return;
    }
    
    // Remove all non-digit characters for validation
    const digitsOnly = value.replace(/\D/g, "");
    
    // Just check if it has 10 digits (US format) - less strict validation
    if (digitsOnly.length === 10) {
      setPhoneError("");
      return;
    }
    
    // Also allow 11 digits if it starts with 1
    if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
      setPhoneError("");
      return;
    }
    
    // Allow international format
    if (value.startsWith("+") && digitsOnly.length >= 10) {
      setPhoneError("");
      return;
    }
    
    setPhoneError("Please enter a valid 10-digit phone number");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!email && !phoneNumber) {
      if (email) validateEmailField(email);
      if (phoneNumber) validatePhoneField(phoneNumber);
      return;
    }

    if (email && !validateEmail(email)) {
      setEmailError("Invalid email address");
      return;
    }

    let normalizedPhoneNumber = phoneNumber;
    if (phoneNumber) {
      // Normalize phone number for validation
      const digitsOnly = phoneNumber.replace(/\D/g, "");
      
      // Validate format (not strict validation - allow test numbers)
      if (digitsOnly.length < 10 || digitsOnly.length > 15) {
        setPhoneError("Please enter a valid phone number");
        return;
      }
      
      // Normalize to international format for submission
      if (phoneNumber.startsWith("+")) {
        normalizedPhoneNumber = phoneNumber;
      } 
      // If it's 10 digits (US format), add +1 prefix
      else if (digitsOnly.length === 10) {
        normalizedPhoneNumber = `+1${digitsOnly}`;
      } 
      // If it's 11 digits starting with 1, add + prefix
      else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
        normalizedPhoneNumber = `+${digitsOnly}`;
      }
      // For other formats, try to use as-is if it starts with +
      else if (!phoneNumber.startsWith("+")) {
        // If we can't normalize, use the original
        normalizedPhoneNumber = phoneNumber;
      }
    }

    const onSuccess = () => {
      navigate("/submission-thank-you?type=contact");
    };

    requestDemo({ name, email, phoneNumber: normalizedPhoneNumber }, onSuccess);
    setName("");
    setEmail("");
    setPhoneNumber("");
    setEmailError("");
    setPhoneError("");
  };

  // Remove the local phone number constants since we're now using the ones from constants.js

  return (
    <ContactUsSectionWrapper id={HOMEPAGE_ANCHOR_CONTAINER_IDs.contactUs.key}>
      <HeroWrapper>
        <BackgroundImage
          src="/images/backgrounds/hero-bg-1.webp"
          alt=""
          $blend="plus-lighter"
        />
        <BackgroundImage
          src="/images/backgrounds/hero-bg-1.webp"
          alt=""
          $opacity={0.6}
          $blend="soft-light"
        />
        <BackgroundBlur />

        <ContentWrapper
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Heading>
            Ready to elevate your campaign?
            <br />
            Let's connect
            <DecorativeDot
              src="/images/icons/dot-white.svg"
              alt="Decorative dots"
            />
          </Heading>

          <FormContainer>
            <FormHeading>Contact us</FormHeading>
            <FormInner onSubmit={handleSubmit}>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={(e) => setName(e.target.value.trim())}
                placeholder="Name"
                required
              />
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                onBlur={(e) => {
                  const trimmed = e.target.value.trim();
                  setEmail(trimmed);
                  validateEmailField(trimmed);
                }}
                placeholder="Email"
                $hasError={!!emailError}
              />
              <Input
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setPhoneNumber(formatted);
                  setPhoneError("");
                }}
                onBlur={(e) => {
                  const trimmed = e.target.value.trim();
                  setPhoneNumber(trimmed);
                  validatePhoneField(trimmed);
                }}
                placeholder="Phone number"
                $hasError={!!phoneError}
              />
              <Button type="submit" disabled={hasErrors}>
                Request a Demo
                <img
                  src="/images/icons/arrow-top-right-white.svg"
                  alt="Arrow right"
                />
              </Button>
            </FormInner>
            <ContactsSection>
              <ContactItem>
                <ContactIconWrapper>
                  <img src={EnvelopeIconUrl} alt="Email" />
                </ContactIconWrapper>
                <ContactLink href={`mailto:${CONTACT_EMAIL}`}>
                  {CONTACT_EMAIL}
                </ContactLink>
              </ContactItem>
              <ContactItem>
                <ContactIconWrapper>
                  <img src={MapPinIconUrl} alt="Address" />
                </ContactIconWrapper>
                <ContactLink>{CONTACT_ADDRESS_SHORT_STR}</ContactLink>
              </ContactItem>
              <ContactItem>
                <ContactIconWrapper>
                  <img src={PhoneIconUrl} alt="Phone" />
                </ContactIconWrapper>
                <ContactLink href={`tel:${CONTACT_PHONE_NUMBER_RAW}`}>
                  {CONTACT_PHONE_NUMBER_DISPLAY}
                </ContactLink>
              </ContactItem>
            </ContactsSection>
          </FormContainer>
        </ContentWrapper>
      </HeroWrapper>
    </ContactUsSectionWrapper>
  );
}
